import { SearchOutlined } from '@ant-design/icons';
import { Input, Spin, Tag, Typography } from 'antd';
import type { CSSProperties } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type CustomElement,
  type CustomElementType,
  customElementsApi,
} from '@/api/customElements';
import { useCurrentUserEmail } from '@/auth/useCurrentUserEmail';

const { Text } = Typography;

type Props = {
  /** Which kinds to show — `['Field', 'Section']` for the field palette, `['Step']` for the Add Step modal. */
  types: CustomElementType[];
  /** Highlights a Field card as "armed" — mirrors the built-in palette's click-to-arm affordance. */
  armedFieldId?: string | null;
  onPickField?: (el: CustomElement) => void;
  onPickSection?: (el: CustomElement) => void;
  onPickStep?: (el: CustomElement) => void;
};

const CARD_BASE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 12px',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  background: '#fff',
  textAlign: 'left',
  width: '100%',
  cursor: 'pointer',
};

const KIND_COLOR: Record<CustomElementType, string> = {
  Field: 'blue',
  Section: 'purple',
  Step: 'orange',
};

/**
 * Shared "Custom" tab body for the field palette and the Add Step modal — fetches every
 * custom element once, filters to the requested kinds, and renders the originator's own
 * elements first (then a divider, then everyone else's), with a search box on top. The BE
 * already returns mine-first ordering; this just needs to know where to put the divider.
 */
export function CustomElementTabContent({
  types,
  armedFieldId,
  onPickField,
  onPickSection,
  onPickStep,
}: Props) {
  const { t } = useTranslation();
  const myEmail = useCurrentUserEmail();
  const [elements, setElements] = useState<CustomElement[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    customElementsApi
      .list()
      .then((list) => {
        if (alive) setElements(list);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (elements ?? [])
      .filter((el) => types.includes(el.type))
      .filter(
        (el) =>
          needle === '' ||
          el.nameEn.toLowerCase().includes(needle) ||
          el.nameFr.toLowerCase().includes(needle),
      );
  }, [elements, types, search]);

  const mine = filtered.filter((el) => myEmail !== null && el.author === myEmail);
  const others = filtered.filter((el) => !(myEmail !== null && el.author === myEmail));

  const handlePick = (el: CustomElement) => {
    if (el.type === 'Field') onPickField?.(el);
    else if (el.type === 'Section') onPickSection?.(el);
    else onPickStep?.(el);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <Input
        placeholder={t('workflow.customElements.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
        size="small"
        suffix={<SearchOutlined style={{ color: '#6b7280' }} />}
      />

      {elements === null && !loadError && (
        <div style={{ textAlign: 'center', padding: 16 }}>
          <Spin size="small" />
        </div>
      )}

      {loadError && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {t('workflow.customElements.loadFailed')}
        </Text>
      )}

      {elements !== null && !loadError && filtered.length === 0 && (
        <Text type="secondary" style={{ fontSize: 12, padding: '0 4px' }}>
          {t('workflow.customElements.empty')}
        </Text>
      )}

      {mine.length > 0 && (
        <CardGroup
          headerKey="workflow.customElements.mineHeader"
          items={mine}
          armedFieldId={armedFieldId}
          onPick={handlePick}
        />
      )}

      {mine.length > 0 && others.length > 0 && <div style={{ borderTop: '1px solid #f1f5f9' }} />}

      {others.length > 0 && (
        <CardGroup
          headerKey="workflow.customElements.othersHeader"
          items={others}
          armedFieldId={armedFieldId}
          onPick={handlePick}
        />
      )}
    </div>
  );
}

function CardGroup({
  headerKey,
  items,
  armedFieldId,
  onPick,
}: {
  headerKey: string;
  items: CustomElement[];
  armedFieldId?: string | null;
  onPick: (el: CustomElement) => void;
}) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Text
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#6b7280',
          letterSpacing: 0.6,
          textTransform: 'uppercase',
        }}
      >
        {t(headerKey)}
      </Text>
      {items.map((el) => {
        const armed = el.type === 'Field' && armedFieldId === el.id;
        return (
          <button
            key={el.id}
            type="button"
            onClick={() => onPick(el)}
            style={{
              ...CARD_BASE,
              background: armed ? '#EFF6FF' : '#fff',
              borderColor: armed ? '#0179C1' : '#e5e7eb',
            }}
          >
            <Tag color={KIND_COLOR[el.type]} style={{ margin: 0, flexShrink: 0 }}>
              {t(`workflow.customElements.kind${el.type}`)}
            </Tag>
            <Text style={{ fontSize: 13, color: '#111827', flex: 1, minWidth: 0 }} ellipsis>
              {el.nameEn}
            </Text>
          </button>
        );
      })}
    </div>
  );
}
